<template>
    <div class="mnc" :class="{ 'is-editing': editing, 'is-flash': flash }">
        <input
            v-if="editing"
            ref="input"
            v-model="draft"
            type="text"
            maxlength="500"
            class="mnc__input poppinsRegular"
            :placeholder="$t('stock.movements.note_placeholder')"
            :aria-label="$t('stock.movements.columns.note')"
            @keydown.enter.prevent="save"
            @keydown.esc.prevent.stop="cancel"
            @blur="save"
        />
        <template v-else-if="editable">
            <!-- Note existante : crayon violet au survol de la ligne, bloc surligné au survol du bouton -->
            <button
                v-if="value"
                type="button"
                class="mnc__note"
                :title="$t('stock.movements.edit_note')"
                :aria-label="`${$t('stock.movements.edit_note')} : ${value}`"
                @click.stop="startEdit"
            >
                <span class="mnc__text">{{ value }}</span>
                <svg
                    class="mnc__pencil"
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="#7d2dbd"
                    stroke-width="1.5"
                    aria-hidden="true"
                >
                    <path d="M10.5 2.5l3 3-7 7h-3v-3z" />
                </svg>
            </button>
            <!-- Pas de note : « + Note » révélé au survol de la ligne (ou au focus clavier) -->
            <button v-else type="button" class="mnc__add" @click.stop="startEdit">
                {{ $t('stock.movements.add_note') }}
            </button>
        </template>
        <span v-else-if="value" class="mnc__readonly" :title="value">{{ value }}</span>
    </div>
</template>

<script>
const FLASH_DURATION = 1200

export default {
    name: 'MovementNoteCell',
    props: {
        value: {
            type: String,
            default: null,
        },
        /** Vient du serveur (permission) : faux ⇒ lecture seule, ni « + Note » ni crayon. */
        editable: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            editing: false,
            draft: '',
            flash: false,
            saving: false,
        }
    },
    methods: {
        startEdit() {
            if (!this.editable) return
            this.draft = this.value || ''
            this.editing = true
            this.saving = false
            this.$nextTick(() => {
                const input = this.$refs.input
                if (input) {
                    input.focus()
                    input.select()
                }
            })
        },
        save() {
            if (!this.editing || this.saving) return
            this.saving = true // blur + Enter peuvent se suivre : un seul enregistrement
            const next = this.draft.trim()
            const previous = this.value || ''
            this.editing = false
            if (next !== previous) {
                this.$emit('save', next === '' ? null : next)
                this.flash = true
                clearTimeout(this._flashTimer)
                this._flashTimer = setTimeout(() => {
                    this.flash = false
                }, FLASH_DURATION)
            }
        },
        cancel() {
            this.saving = true // le blur qui suit ne doit pas enregistrer
            this.editing = false
            this.draft = this.value || ''
        },
    },
    beforeDestroy() {
        clearTimeout(this._flashTimer)
    },
}
</script>

<style scoped lang="scss">
// Valeurs reprises de la maquette PO « Mouvements de stock.html »
.mnc {
    min-width: 140px;
    border-radius: 8px;

    &.is-flash .mnc__note {
        animation: mnc-flash 1.2s ease-out;
    }
}

.mnc__note {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    height: 32px;
    box-sizing: border-box;
    padding: 0 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    text-align: left;
    font-size: 13px;
    color: #6b7480;
    transition:
        background-color 0.12s ease,
        border-color 0.12s ease,
        color 0.12s ease;

    &:hover,
    &:focus-visible {
        background: #f3eafa;
        border-color: #e2d0f3;
        color: #3f1761;
        outline: none;
    }
}

.mnc__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mnc__pencil {
    flex: none;
    opacity: 0;
    transition: opacity 0.12s ease;
}

.mnc__add {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 32px;
    padding: 0 10px;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    color: #7d2dbd;
    opacity: 0;
    transition:
        opacity 0.12s ease,
        background-color 0.12s ease;

    &:hover,
    &:focus-visible {
        background: #f3eafa;
        color: #571f84;
        outline: none;
    }

    &:focus-visible {
        opacity: 1;
        box-shadow: 0 0 0 2px #7d2dbd;
    }
}

// Révélés au survol de la ligne (le scope ne s'applique qu'au dernier sélecteur composé)
tr:hover .mnc__add,
tr:hover .mnc__pencil,
.mnc__note:focus-visible .mnc__pencil {
    opacity: 1;
}

// Spec : « au survol de la ligne, un crayon violet apparaît à droite du texte, dans un bloc
// surligné » — le bloc suit la ligne, pas seulement le bouton.
tr:hover .mnc__note {
    background: #f3eafa;
    border-color: #e2d0f3;
    color: #3f1761;
}

.mnc__readonly {
    display: inline-block;
    max-width: 100%;
    padding: 0 10px;
    font-size: 13px;
    line-height: 32px;
    color: #6b7480;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}

.mnc__input {
    position: relative;
    width: 100%;
    min-width: 180px;
    height: 32px;
    box-sizing: border-box;
    padding: 0 10px;
    border: 1px solid #ab6edc;
    border-radius: 8px;
    font-size: 13px;
    color: #021832;
    background: #fff;
    outline: none;
}

.dark .mnc__input {
    background: #1a1f37;
    color: #fff;
}

@keyframes mnc-flash {
    from {
        background-color: #dcfce7;
    }
    to {
        background-color: transparent;
    }
}

// Écrans tactiles : pas de survol, on garde les actions visibles
@media (hover: none) {
    .mnc__add,
    .mnc__pencil {
        opacity: 1;
    }
}
</style>
